import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  Form,
  Loader,
  Modal,
  PLATFORM_THEME_COLORS,
  SelectInput,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetAllMembers } from 'src/hooks/members/get-members.hook';
import { useChangeProjectOwners } from 'src/hooks/projects/change-projects-owners.hook';

import { TransferProjectsModalProps } from './transfer-projects.modal.interfaces';
import { WarningContainer, HeaderContainer } from './transfer-projects-modal.styles';

export const TransferProjectsModal: React.FC<TransferProjectsModalProps> = ({
  projects,
  selectedEmployee,
  onClose,
  onFinish,
}) => {
  const { t } = useTranslation(['common', 'employee']);
  const [isCloseModalAttempt, setIsCloseModalAttempt] = useState(false);

  const currentCompanyId = useGetCurrentCompanyId();
  const { members, loading } = useGetAllMembers({ companyId: currentCompanyId });
  const filteredMembers = members.filter(
    ({ companyPosition }: any) => companyPosition && companyPosition.name === 'Project Owner'
  );
  const changeProjectOwners = useChangeProjectOwners({ onSuccess: onClose });

  const closeModalAttempt = () => {
    setIsCloseModalAttempt(prev => !prev);
    if (isCloseModalAttempt) {
      onClose();
    }
  };

  const onFinishHandler = (values: any) => {
    const payload = Object.keys(values).map(key => ({
      projectId: key,
      memberId: values[key],
    }));

    changeProjectOwners({ variables: { payload } });
    onFinish();
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Modal isOpened onClose={onClose} title={t('employee:transferModal.title')}>
      <HeaderContainer>
        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
          {t('employee:transferModal.description')}
        </Typography>

        <Typography>
          {t('employee:transferModal.numberOfProjects', { number: projects.length })}
        </Typography>
      </HeaderContainer>

      {isCloseModalAttempt && (
        <WarningContainer>
          <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.tone.red}>
            {t('employee:transferModal.closeAttemptTitle')}
          </Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t('employee:transferModal.closeAttemptDescription')}
          </Typography>
        </WarningContainer>
      )}

      <Form layout="vertical" onFinish={onFinishHandler}>
        {projects.map(project => (
          <Form.Item
            name={project._id}
            key={project._id}
            label={project.name}
            rules={[{ required: true }]}
          >
            <SelectInput placeholder={t('employee:labels.chooseEmployee')}>
              {filteredMembers
                .filter(
                  (member: any) =>
                    member._id !== selectedEmployee._id && project.memberDeputyId !== member._id
                )
                .map((member: any) => (
                  <SelectInput.Option key={member._id} value={member._id}>
                    {member.profile.firstName} {member.profile.lastName}
                  </SelectInput.Option>
                ))}
            </SelectInput>
          </Form.Item>
        ))}
        <ButtonsWrapper>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={() => closeModalAttempt()}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};
